import { useState, useRef, useEffect } from 'react';
import Modal from './Modal.jsx';
import Button from './Button.jsx';
import FormField from './FormField.jsx';
import { useCreateCategory } from '../hooks/useCategories.js';

const MATCH_TYPE_OPTIONS = [
  { value: 'label_contains', label: 'Libellé contient' },
  { value: 'beneficiary_equals', label: 'Bénéficiaire égal à' },
];

const EMPTY_FORM = {
  name: '',
  createRule: true,
  matchType: 'label_contains',
  matchValue: '',
};

export default function CategoryCustomModal({ parentCategory, rootCategory, open, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const nameRef = useRef(null);
  const create = useCreateCategory();

  // Autofocus name input when modal opens
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      create.reset?.();
      // Small timeout to allow modal render before focusing
      const t = setTimeout(() => nameRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const body = {
      name: form.name.trim(),
      parent_id: parentCategory.id,
    };
    if (form.createRule && form.matchValue.trim()) {
      body.create_rule = {
        match_type: form.matchType,
        match_value: form.matchValue.trim(),
      };
    }
    try {
      await create.mutateAsync(body);
      onClose();
    } catch {
      // Error displayed via create.error
    }
  }

  const headerLabel = rootCategory && parentCategory
    ? `${rootCategory.name} › ${parentCategory.name}`
    : parentCategory?.name ?? '';

  const isSubmitDisabled = !form.name.trim() || (form.createRule && !form.matchValue.trim());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nouvelle sous-catégorie"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Locked parent display */}
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
          <p className="text-xs text-slate-500 mb-0.5">Sous</p>
          <p className="text-sm font-medium text-slate-800">{headerLabel}</p>
        </div>

        {/* Name */}
        <FormField label="Nom" htmlFor="ccm-name" required>
          <input
            ref={nameRef}
            id="ccm-name"
            type="text"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            maxLength={100}
            required
            placeholder="ex : Google Ads"
            className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
          />
        </FormField>

        {/* Auto rule section */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.createRule}
              onChange={(e) => set('createRule', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
            />
            <span className="text-sm font-medium text-slate-700">
              Créer une règle d'attribution automatique
            </span>
          </label>

          {form.createRule && (
            <div className="ml-6 space-y-3 border-l-2 border-slate-100 pl-4">
              <FormField label="Type de correspondance" htmlFor="ccm-match-type">
                <select
                  id="ccm-match-type"
                  value={form.matchType}
                  onChange={(e) => set('matchType', e.target.value)}
                  className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                >
                  {MATCH_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </FormField>

              <FormField
                label="Valeur"
                htmlFor="ccm-match-value"
                required={form.createRule}
                hint='Ex : "google ads" — insensible à la casse.'
              >
                <input
                  id="ccm-match-value"
                  type="text"
                  value={form.matchValue}
                  onChange={(e) => set('matchValue', e.target.value)}
                  required={form.createRule}
                  placeholder="google ads"
                  className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                />
              </FormField>

              <p className="text-xs text-slate-500 leading-relaxed">
                Les transactions existantes seront reclassées au prochain sync Qonto (max 15 min)
                ou immédiatement via le bouton «&nbsp;Synchroniser&nbsp;» dans Paramètres.
              </p>
            </div>
          )}
        </div>

        {/* Mutation error */}
        {create.error && (
          <div
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            role="alert"
          >
            {create.error.message}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} disabled={create.isPending}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            loading={create.isPending}
          >
            Créer
          </Button>
        </div>
      </form>
    </Modal>
  );
}
